import { useEffect } from 'react';
import { Box } from '@nimbus-ds/components';
import {
  BoxPackedIcon,
  MoneyIcon,
  TruckIcon,
  StoreIcon,
} from '@nimbus-ds/icons';
import { FEATURE_SEARCH_FILTER } from 'App/features';
import {
  MIGRATION_ENABLE_ALL_MIGRATED_PAGES,
  MIGRATION_ORDERS,
} from 'App/featuresFlags';
import { BlockWithTitle, useResponsive } from 'commons/components';
import {
  useGetTags,
  useIsMobileDevice,
  useStatusFeatureByKey,
} from 'domains/Auth/hooks';
import { useTranslationDashboard } from 'domains/Dashboard/hooks';
import { ordersRoutes } from 'domains/Orders';
import { useSavedSearches } from 'domains/Orders/components/SavedSearches';
import { useGetPendingOrders } from 'domains/Orders/Orders/hooks';
import { DefaultSavedSearchesCode } from 'domains/Orders/Orders/ordersSlice/savedSearches';
import PendingOrdersCard from './PendingOrdersCard';
import { ORDER_FILTERS, handleClick } from './pendingOrdersUtils';

export type PendingOrderType =
  | 'pendingPayment'
  | 'pendingShipping'
  | 'pendingPacked';

function PendingOrders() {
  const { pendingOrders, isLoading, isError } = useGetPendingOrders();
  const { fetchSavedSearchesCount } = useSavedSearches();
  const { isAvailable: isFiltersAllowed } = useStatusFeatureByKey(
    FEATURE_SEARCH_FILTER,
  );
  const isMobileDevice = useIsMobileDevice();
  const { isMobile: isMobileResponsive } = useResponsive();
  const tags = useGetTags();
  const {
    buildQueryParamsFromSavedSearch,
    savedSearchesCount,
    savedSearchesCountStatus,
  } = useSavedSearches();
  const t = useTranslationDashboard(`pendingOrdersPromote`);

  useEffect(() => {
    fetchSavedSearchesCount();
  }, [fetchSavedSearchesCount]);

  if (isError || savedSearchesCountStatus.isError) return null;

  const hasOrdersAccess =
    tags.includes(MIGRATION_ORDERS) ||
    tags.includes(MIGRATION_ENABLE_ALL_MIGRATED_PAGES);
  const shouldGoToOldAdmin = !hasOrdersAccess && !isMobileDevice;

  const getFilterPath = (code: DefaultSavedSearchesCode) => {
    if (shouldGoToOldAdmin) {
      return `/admin${ORDER_FILTERS.old[code]}`;
    }
    return `${ordersRoutes.orderList}${
      isFiltersAllowed ? `?page=1&${buildQueryParamsFromSavedSearch(code)}` : ''
    }`;
  };

  const paymentPendingCount =
    savedSearchesCount?.[DefaultSavedSearchesCode.PAYMENT_PENDING]?.count;
  const readyToPackCount =
    savedSearchesCount?.[DefaultSavedSearchesCode.READY_TO_PACK]?.count;
  const readyToSendCount =
    savedSearchesCount?.[DefaultSavedSearchesCode.READY_TO_SEND]?.count;
  const pickupCount =
    savedSearchesCount?.[DefaultSavedSearchesCode.READY_TO_PICKUP]?.count;

  const emptyPayment = paymentPendingCount === 0;
  const emptyUnpacked = readyToPackCount === 0;
  const emptyShipping = readyToSendCount === 0;
  const emptyPickup = pickupCount === 0;

  const isLoadingCounts = isLoading || savedSearchesCountStatus.isLoading;

  const getTitle = (isEmpty: boolean, count?: number) =>
    isEmpty ? t('pendingEmpty') : t('orders', { count });

  const iconSize = 14;

  return (
    <BlockWithTitle title="">
      <Box
        display={!isMobileResponsive ? 'flex' : 'grid'}
        gridTemplateColumns={isMobileResponsive ? 'repeat(2, 1fr)' : undefined}
        gridGap="3"
        flexDirection="row"
        justifyContent="space-between"
        alignItems="stretch"
        gap="4"
        height={!isMobileResponsive ? '3.625rem' : undefined}
      >
        <PendingOrdersCard
          icon={<MoneyIcon size={iconSize} />}
          subtitle={t('pendingPayment')}
          title={getTitle(
            emptyPayment,
            shouldGoToOldAdmin
              ? pendingOrders?.paymentOldAdminResult
              : paymentPendingCount,
          )}
          skeleton={isLoadingCounts}
          to={getFilterPath(DefaultSavedSearchesCode.PAYMENT_PENDING)}
          onClick={handleClick(DefaultSavedSearchesCode.PAYMENT_PENDING)}
          hideIcon={!isFiltersAllowed || emptyPayment}
          shouldGoToOldAdmin={shouldGoToOldAdmin}
        />
        <PendingOrdersCard
          icon={<BoxPackedIcon size={iconSize} />}
          subtitle={t('pendingPacked')}
          title={getTitle(
            emptyUnpacked,
            shouldGoToOldAdmin
              ? pendingOrders?.unpackedOldAdminResult
              : readyToPackCount,
          )}
          skeleton={isLoadingCounts}
          to={getFilterPath(DefaultSavedSearchesCode.READY_TO_PACK)}
          onClick={handleClick(DefaultSavedSearchesCode.READY_TO_PACK)}
          hideIcon={!isFiltersAllowed || emptyUnpacked}
          shouldGoToOldAdmin={shouldGoToOldAdmin}
        />
        <PendingOrdersCard
          icon={<TruckIcon size={iconSize} />}
          subtitle={
            shouldGoToOldAdmin ? t('oldPendingShipping') : t('pendingShipping')
          }
          title={getTitle(
            emptyShipping,
            shouldGoToOldAdmin
              ? pendingOrders?.shippingOldAdminResult
              : readyToSendCount,
          )}
          skeleton={isLoadingCounts}
          to={getFilterPath(DefaultSavedSearchesCode.READY_TO_SEND)}
          onClick={handleClick(DefaultSavedSearchesCode.READY_TO_SEND)}
          hideIcon={!isFiltersAllowed || emptyShipping}
          shouldGoToOldAdmin={shouldGoToOldAdmin}
        />
        <PendingOrdersCard
          icon={<StoreIcon size={iconSize} />}
          subtitle={t('pendingPickup')}
          title={getTitle(emptyPickup, pickupCount)}
          skeleton={isLoadingCounts}
          to={getFilterPath(DefaultSavedSearchesCode.READY_TO_PICKUP)}
          onClick={handleClick(DefaultSavedSearchesCode.READY_TO_PICKUP)}
          hideIcon={!isFiltersAllowed || emptyPickup}
          shouldGoToOldAdmin={shouldGoToOldAdmin}
        />
      </Box>
    </BlockWithTitle>
  );
}

export default PendingOrders;
